import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Button,
  Typography,
  Grid,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  Chip,
  Alert,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import axios from "axios";
import { setupManagementUrl, salesManagement } from "../../../config";

const WarehouseDistribution = ({ open, onClose, data, onAction }) => {
  const [rows, setRows] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [baseQuantity, setBaseQuantity] = useState(0);
  const [remainingQuantity, setRemainingQuantity] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const swalTop = Swal.mixin({
  didOpen: () => {
    const swalContainer = document.querySelector(".swal2-container");
    if (swalContainer) {
      swalContainer.style.zIndex = "99999"; // or higher than your MUI modal
    }
  },
});

  // Extract product details
  const productCode = data?.productCode || "";
  const productName = data?.productName || "";
  const aliasProductName = data?.aliasProductName || "";
  const defaultCartons = data?.cartons || "";

  // Fetch colors from API
  const fetchColors = async () => {
    try {
      const rawToken = localStorage.getItem("token");
      const cleanToken = rawToken?.replace(/^"|"$/g, "") || "";

      const response = await axios.post(
        setupManagementUrl.uri,
        {
          query: `
            query {
              color {
                id
                name
              }
            }
          `,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${cleanToken}`,
          },
        }
      );

      if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
      }

      return response.data.data?.color || [];
    } catch (error) {
      console.error("Error fetching colors:", error);
      swalTop.fire({
        icon: "error",
        title: "Failed to Load Colors",
        text: error.response?.data?.errors?.[0]?.message || error.message,
      });
      return [];
    }
  };

  // Initialize on dialog open
  useEffect(() => {
    if (open) {
      const quantity = Number(data?.quantity) || 0;
      setBaseQuantity(quantity);
      setRemainingQuantity(quantity);

      // Initialize with one row, prefilled with cartons if provided
      setRows([
        {
          id: 1,
          colorId: "",
          colorName: "",
          cartons: defaultCartons || "",
          quantityPerCarton: "",
          minSize: "",
          maxSize: "",
        },
      ]);

      fetchColors().then((colors) => {
        if (Array.isArray(colors)) {
          setAvailableColors(colors);
        }
      });
    }
  }, [open, data]);

  // Calculate remaining quantity whenever rows change
  const calculateTotals = (updatedRows) => {
    const totalUsed = updatedRows.reduce((acc, row) => {
      const cartons = Number(row.cartons) || 0;
      const perCarton = Number(row.quantityPerCarton) || 0;
      return acc + cartons * perCarton;
    }, 0);

    const remaining = Math.max(baseQuantity - totalUsed, 0);
    setRemainingQuantity(remaining);
  };

  // Add new row
  const addRow = () => {
    const newId = rows.length > 0 ? Math.max(...rows.map((r) => r.id)) + 1 : 1;
    const newRows = [
      ...rows,
      {
        id: newId,
        colorId: "",
        colorName: "",
        cartons: defaultCartons || "",
        quantityPerCarton: "",
        minSize: "",
        maxSize: "",
      },
    ];
    setRows(newRows);
  };

  // Remove row
  const removeRow = (id) => {
    if (rows.length > 1) {
      const updatedRows = rows.filter((row) => row.id !== id);
      setRows(updatedRows);
      calculateTotals(updatedRows);
    }
  };

  // Update row field
  const updateRow = (id, field, value) => {
    const updatedRows = rows.map((row) => {
      if (row.id === id) {
        // If updating color, store both id and name
        if (field === "color") {
          const selectedColor = availableColors.find((c) => c.id === value);
          return {
            ...row,
            colorId: value,
            colorName: selectedColor?.name || "",
          };
        }
        
        // If updating cartons or quantityPerCarton, validate against available quantity
        if (field === "cartons" || field === "quantityPerCarton") {
          const updatedRow = { ...row, [field]: value };
          const newCartons = Number(updatedRow.cartons) || 0;
          const newQtyPerCarton = Number(updatedRow.quantityPerCarton) || 0;
          const newRowTotal = newCartons * newQtyPerCarton;
          
          // Calculate total from OTHER rows (excluding current row)
          const otherRowsTotal = rows.reduce((acc, r) => {
            if (r.id === id) return acc;
            const cartons = Number(r.cartons) || 0;
            const perCarton = Number(r.quantityPerCarton) || 0;
            return acc + cartons * perCarton;
          }, 0);
          
          // Check if this would exceed base quantity
          if (otherRowsTotal + newRowTotal > baseQuantity) {
            const maxAllowedForRow = baseQuantity - otherRowsTotal;
            swalTop.fire({
              icon: "warning",
              title: "Quantity Exceeded",
              text: `This row can only distribute ${maxAllowedForRow} units. You're trying to distribute ${newRowTotal} units.`,
              timer: 3000,
            });
            return row; // Don't update the row
          }
          
          return updatedRow;
        }
        
        return { ...row, [field]: value };
      }
      return row;
    });

    setRows(updatedRows);
    calculateTotals(updatedRows);
  };

  // Get row validation state
  const getRowValidation = (row) => {
    const rowTotal = (Number(row.cartons) || 0) * (Number(row.quantityPerCarton) || 0);
    
    // Calculate total from other rows
    const otherRowsTotal = rows.reduce((acc, r) => {
      if (r.id === row.id) return acc;
      const cartons = Number(r.cartons) || 0;
      const perCarton = Number(r.quantityPerCarton) || 0;
      return acc + cartons * perCarton;
    }, 0);
    
    const maxAllowed = baseQuantity - otherRowsTotal;
    const isExceeding = rowTotal > maxAllowed;
    
    return { rowTotal, maxAllowed, isExceeding };
  };

  // Get already selected color IDs
  const getSelectedColorIds = (currentRowId) => {
    return rows
      .filter((row) => row.id !== currentRowId && row.colorId)
      .map((row) => row.colorId);
  };

  // Validate all rows before submission
  const validateRows = () => {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      
      // Check for exceeding quantity
      const validation = getRowValidation(row);
      if (validation.isExceeding) {
        swalTop.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Row ${i + 1} exceeds the available quantity. Maximum allowed: ${validation.maxAllowed} units`,
        });
        return false;
      }
      
      if (!row.colorId) {
        swalTop.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Please select a color for row ${i + 1}`,
        });
        return false;
      }
      if (!row.cartons || Number(row.cartons) <= 0) {
        swalTop.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Please enter valid cartons for row ${i + 1}`,
        });
        return false;
      }
      if (!row.quantityPerCarton || Number(row.quantityPerCarton) <= 0) {
        swalTop.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Please enter valid quantity per carton for row ${i + 1}`,
        });
        return false;
      }
    }

    if (remainingQuantity !== 0) {
      swalTop.fire({
        icon: "warning",
        title: "Incomplete Distribution",
        text: `You have ${remainingQuantity} units remaining. Please distribute all quantities.`,
      });
      return false;
    }

    return true;
  };

  // Submit distribution
  const handleSubmit = async () => {
    if (!validateRows()) return;

    setIsSubmitting(true);
    try {
      const rawToken = localStorage.getItem("token");
      const cleanToken = rawToken?.replace(/^"|"$/g, "") || "";

      const decodedTokenStr = localStorage.getItem("decodedToken");
      const decodedToken = decodedTokenStr ? JSON.parse(decodedTokenStr) : {};
      const createdBy = decodedToken.id;

      const purchaseOrderId = data.purchaseOrderId;
      const PurchaseOrderItemId = data.id;
      const distributionData = rows.map((row) => ({
        colorId: row.colorId,
        totalquantity: Number(row.cartons) * Number(row.quantityPerCarton),
        cartons: Number(row.cartons),
        quantityPerCarton: Number(row.quantityPerCarton),
        
      }));

      const mutation = `
        mutation WarehouseDistribution {
          warehouseDistribution(
            newWarehouseDistribution: {
              purchaseOrderId: ${purchaseOrderId},
              purchaseOrderItemsId: ${PurchaseOrderItemId},
              distributionData: """${JSON.stringify(distributionData)}""",
            }
          ) {
            purchaseOrderId
            distributionData
            createdBy
          }
        }
      `;

      const response = await axios.post(
        salesManagement.uri,
        { query: mutation },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${cleanToken}`,
          },
        }
      );

      if (response.status ===200) {
       
      await swalTop.fire({
        icon: "success",
        title: "Success!",
        text: `${data.productName}  with code ${data.productCode} has been distributed successfully, you can view the distribution in Warehouse Inventory> actions> View Distribution. Thank you.`,
      });

      onAction?.();
      handleClose();
    }
    } catch (error) {
      console.error("Error creating warehouse distribution:", error);
      swalTop.fire({
        icon: "error",
        title: "Error!",
        text: error.response.data.errors[0].message || "An error occurred while saving warehouse distribution.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const isFormValid = 
    remainingQuantity === 0 && 
    rows.every((row) => {
      const validation = getRowValidation(row);
      return row.colorId && row.cartons && row.quantityPerCarton && !validation.isExceeding;
    });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            Warehouse Distribution
          </Typography>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Product Information Section */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Product Code
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {productCode || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Product Name
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {productName || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Alias Name
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {aliasProductName || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Quantity
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {baseQuantity}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Remaining Quantity Alert */}
        {remainingQuantity > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{remainingQuantity}</strong> units remaining to distribute
          </Alert>
        )}

        {remainingQuantity === 0 && rows.length > 0 && isFormValid && (
          <Alert severity="success" sx={{ mb: 2 }}>
            All quantities distributed successfully!
          </Alert>
        )}

        {rows.some((row) => getRowValidation(row).isExceeding) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Warning:</strong> One or more rows exceed the available quantity. Please adjust the values.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Distribution Rows */}
        <Box sx={{ mt: 2 }}>
          {rows.map((row, index) => (
            <Grid
              container
              spacing={2}
              key={row.id}
              alignItems="center"
              sx={{
                mb: 2,
                p: 2,
                bgcolor: "#fafafa",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
              }}
            >
              <Grid item xs={12} sm={1}>
                <Chip label={index + 1} color="primary" size="small" />
              </Grid>

              <Grid item xs={12} sm={2.5}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Color</InputLabel>
                  <Select
                    value={row.colorId}
                    label="Color"
                    onChange={(e) => updateRow(row.id, "color", e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select color</em>
                    </MenuItem>
                    {availableColors.map((color) => {
                      const isDisabled = getSelectedColorIds(row.id).includes(color.id);
                      return (
                        <MenuItem
                          key={color.id}
                          value={color.id}
                          disabled={isDisabled}
                        >
                          {color.name} {isDisabled && "(Already selected)"}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Cartons"
                  required
                  value={row.cartons}
                  onChange={(e) => updateRow(row.id, "cartons", e.target.value)}
                  inputProps={{ min: 1 ,readOnly:true}}
                  error={getRowValidation(row).isExceeding}
                  helperText={
                    getRowValidation(row).isExceeding
                      ? `Max: ${getRowValidation(row).maxAllowed}`
                      : ""
                  }
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Qty/Carton"
                  required
                  value={row.quantityPerCarton}
                  onChange={(e) => updateRow(row.id, "quantityPerCarton", e.target.value)}
                  inputProps={{ min: 1 }}
                  error={getRowValidation(row).isExceeding}
                  helperText={
                    getRowValidation(row).isExceeding
                      ? `Max: ${getRowValidation(row).maxAllowed}`
                      : ""
                  }
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Total Qty"
                  value={(Number(row.cartons) || 0) * (Number(row.quantityPerCarton) || 0)}
                  InputProps={{ readOnly: true }}
                  sx={{ bgcolor: "#f5f5f5" }}
                  error={getRowValidation(row).isExceeding}
                  helperText={
                    getRowValidation(row).isExceeding
                      ? "Exceeds limit!"
                      : ""
                  }
                />
              </Grid>

              

              <Grid item xs={12} sm={1}>
                <IconButton
                  onClick={() => removeRow(row.id)}
                  disabled={rows.length === 1}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={addRow}
              variant="outlined"
              disabled={remainingQuantity === 0}
            >
              Add Distribution Row
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit Distribution"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarehouseDistribution;